import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { monitoramentoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import DateRangeSelector from '../../components/monitoramento/DateRangeSelector';
import KpiCard from '../../components/monitoramento/KpiCard';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const toISO = (d: Date): string => d.toISOString().slice(0, 10);

/** Cores do semaforo por faixa de risco */
const getSemaforoColor = (faixa: string): string => {
  switch (faixa) {
    case 'verde': return '#27AE60';
    case 'amarelo': return '#F39C12';
    case 'laranja': return '#E67E22';
    case 'vermelho': return '#E74C3C';
    default: return '#9E9E9E';
  }
};

/** Cor do badge de atingimento na tabela */
const getBadgeCor = (pct: number): 'success' | 'warning' | 'error' | 'default' => {
  if (pct >= 100) return 'success';
  if (pct >= 75) return 'warning';
  return 'error';
};

/** Cor da faixa_risco para Chip */
const getFaixaChipColor = (faixa: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (faixa) {
    case 'verde': return 'success';
    case 'amarelo': return 'warning';
    case 'laranja': return 'warning';
    case 'vermelho': return 'error';
    default: return 'default';
  }
};

const MonitoramentoSuspensao: React.FC = () => {
  const { municipioAtivo } = useAuth();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hoje = new Date();
  const tresMesesAtras = new Date();
  tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3);
  const [dataInicio, setDataInicio] = useState(toISO(tresMesesAtras));
  const [dataFim, setDataFim] = useState(toISO(hoje));

  const handleDateChange = (di: string, df: string) => {
    setDataInicio(di);
    setDataFim(df);
  };

  useEffect(() => {
    if (!municipioAtivo?.id) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await monitoramentoService.getSuspensao(
          municipioAtivo.id,
          dataInicio,
          dataFim,
        );

        setData(result);
      } catch (err: any) {
        setError(
          err?.response?.data?.message || 'Erro ao carregar dados de suspensao de incentivos.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [municipioAtivo?.id, dataInicio, dataFim]);

  if (!municipioAtivo) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', px: 3 }}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Nenhum municipio selecionado
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione um municipio para visualizar dados de suspensao de incentivos.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Skeleton variant="text" width="30%" height={48} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mt: 2, mb: 3 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const indicadores: any[] = data?.indicadores || [];
  const projecao: any[] = data?.projecao || [];

  // KPI 1: Indicadores em Risco (faixa_risco != 'verde')
  const indicadoresEmRisco = indicadores.filter((ind: any) => ind.faixa_risco !== 'verde').length;

  // Calcula nivel geral do semaforo (media dos atingimentos)
  const mediaAtingimento = indicadores.length > 0
    ? indicadores.reduce((acc: number, ind: any) => acc + Number(ind.pct_atingimento || 0), 0) / indicadores.length
    : 0;

  const faixas = [25, 50, 75, 100];

  // Preparar dados de projecao para BarChart: por indicador mostrando acumulado_atual vs projecao_3_meses
  const projecaoChartData = projecao.map((p: any) => ({
    indicador: p.indicador,
    acumulado_atual: Number(p.acumulado_atual || 0),
    projecao_3_meses: Number(p.projecao_3_meses || 0),
  }));

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Suspensao de Incentivos
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {municipioAtivo.nome}
          </Typography>
        </Box>
        <DateRangeSelector
          dataInicio={dataInicio}
          dataFim={dataFim}
          onChange={handleDateChange}
        />
      </Box>

      {/* KPI + Semaforo */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <KpiCard
            titulo="Indicadores em Risco"
            valor={`${indicadoresEmRisco} de ${indicadores.length}`}
            icone={<WarningAmberIcon />}
            cor="#E74C3C"
            subtitulo="Indicadores com faixa de risco diferente de verde"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, borderTop: '3px solid #1E3A5F', height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={500} sx={{ mb: 1.5 }}>
              Semaforo de Atingimento
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {faixas.map((faixa) => {
                const ativo = mediaAtingimento >= faixa;
                const cor = faixa <= 25 ? '#E74C3C' : faixa <= 50 ? '#E67E22' : faixa <= 75 ? '#F39C12' : '#27AE60';
                return (
                  <Box
                    key={faixa}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: ativo ? cor : '#E0E0E0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: ativo ? `0 0 8px ${cor}` : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ color: ativo ? '#fff' : '#999', fontSize: 10 }}>
                      {faixa}%
                    </Typography>
                  </Box>
                );
              })}
              <Typography variant="body2" fontWeight={600} sx={{ ml: 1, color: mediaAtingimento >= 75 ? '#27AE60' : mediaAtingimento >= 50 ? '#F39C12' : '#E74C3C' }}>
                {mediaAtingimento.toFixed(1)}%
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabela de indicadores */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Indicadores de Desempenho
        </Typography>
        {indicadores.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Indicador</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Atingido</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Meta</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>% Atingimento</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Faixa de Risco</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {indicadores.map((row: any, index: number) => {
                  const pct = Number(row.pct_atingimento || 0);
                  const atingido = Number(row.atingido || 0);
                  return (
                    <TableRow key={index}>
                      <TableCell>{row.descricao}</TableCell>
                      <TableCell align="right">
                        {atingido.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="right">
                        {(row.meta ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${pct.toFixed(1)}%`}
                          size="small"
                          color={getBadgeCor(pct)}
                          sx={{ fontWeight: 600, minWidth: 70 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.faixa_risco || 'N/A'}
                          size="small"
                          color={getFaixaChipColor(row.faixa_risco)}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            minWidth: 80,
                            borderColor: getSemaforoColor(row.faixa_risco),
                            color: getSemaforoColor(row.faixa_risco),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de indicadores para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {/* Projecao - BarChart por indicador */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Projecao por Indicador
        </Typography>
        {projecaoChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={Math.max(projecaoChartData.length * 50, 300)}>
            <BarChart
              data={projecaoChartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={(v: number) => v.toLocaleString('pt-BR')} />
              <YAxis
                type="category"
                dataKey="indicador"
                width={150}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value: any, name: any) => [
                  Number(value).toLocaleString('pt-BR'),
                  name === 'acumulado_atual' ? 'Acumulado Atual' : 'Projecao 3 Meses',
                ]}
              />
              <Legend
                formatter={(value: string) =>
                  value === 'acumulado_atual' ? 'Acumulado Atual' : 'Projecao 3 Meses'
                }
              />
              <Bar dataKey="acumulado_atual" fill="#1E3A5F" radius={[0, 4, 4, 0]} name="acumulado_atual" />
              <Bar dataKey="projecao_3_meses" fill="#F39C12" radius={[0, 4, 4, 0]} name="projecao_3_meses" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Sem dados de projecao para o periodo selecionado.
          </Typography>
        )}
      </Paper>

      {municipioAtivo && data && (
        <AnalisePredicaoCard
          dadosContexto={JSON.stringify(data)}
          municipioId={municipioAtivo.id}
          painelId="suspensao"
          contexto="dashboard"
        />
      )}

      <ChatIAButton
        contexto="dashboard"
        dadosContexto={JSON.stringify(data)}
        fabSx={{ bottom: 24, right: 24, top: 'auto' }}
      />
    </Box>
  );
};

export default MonitoramentoSuspensao;
